import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    FileText,
    MessageSquareText,
    PenTool,
    Rocket
} from 'lucide-react'

interface Step {
    icon: LucideIcon
    title: string
    description: string
}

const STEPS: Step[] = [
    {
        icon: MessageSquareText,
        title: 'Tell us about your business',
        description: 'Send a quick enquiry with what you do, what you need and the budget you have in mind.'
    },
    {
        icon: FileText,
        title: 'Get a clear quote',
        description: 'We come back with a plan and a price that fits, explained in plain English.'
    },
    {
        icon: PenTool,
        title: 'We design and build',
        description: 'Your website takes shape, and you tell us what to tweak along the way.'
    },
    {
        icon: Rocket,
        title: 'Go live',
        description: 'Your new site launches, ready to work for your business from day one.'
    }
]

const ProcessSection: FC = (): ReactNode => {
    return (
        <section id='process' aria-labelledby='process-title' className='scroll-mt-16 border-t border-border bg-card py-20 text-ink sm:py-28'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <div className='max-w-2xl'>
                        <p className='text-sm font-semibold uppercase tracking-[0.18em] text-ink/55'>
                            How it works
                        </p>
                        <h2
                            id='process-title'
                            className='mt-4 text-balance font-heading text-3xl font-bold leading-tight tracking-[-0.02em] sm:text-5xl'
                        >
                            From first message to launch day.
                        </h2>
                    </div>
                    <p className='max-w-md text-lg leading-relaxed text-muted-foreground'>
                        Four simple steps, and you know what is happening at every one of them.
                    </p>
                </div>

                <ol className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {STEPS.map(({ icon: Icon, title, description }, index) => (
                        <li key={title} className='flex flex-col rounded-[1.75rem] border border-border bg-paper p-7'>
                            <div className='flex items-center justify-between'>
                                <span className='font-heading text-sm font-bold text-ink/40'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>
                                <span className='flex size-11 items-center justify-center rounded-xl bg-ink text-volt'>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>
                            </div>
                            <h3 className='mt-10 font-heading text-lg font-bold leading-snug'>
                                {title}
                            </h3>
                            <p className='mt-3 text-[15px] leading-relaxed text-muted-foreground'>
                                {description}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

export default ProcessSection
